import { Modal, Text } from '@fozito/common-ui';
import clsx from 'clsx';
import { delay, first } from 'lodash';
import cashierApis from 'modules/POS/services/apis';
import { IProductItem } from 'modules/shop/types/product';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import toast from 'utils/toast';
import { setPageLoading } from 'widgets/PageLoading';
import ModalProductInfo from './ModalProductInfo';
import scan_gif from './scan.gif';
import styled from 'styled-components';
import close from './close-circle-fill.png';
import { cashierScannerCtrl } from 'modules/POS/Cashier/cashier.controller';

const Container = styled.label`
  background: #fff;
  width: 300px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-top: 18px;
  border-radius: 8px;
`;
const IconClose = styled.div`
  position: absolute;
  width: 42px;
  height: 42px;
  top: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover,
  &:active {
    opacity: 0.8;
    transform: scale(1.2);
    cursor: pointer;
    transition: all 0.1s;
  }

  &:disabled {
    opacity: 0.7;
    &:hover,
    &:active {
      opacity: 1;
      cursor: not-allowed;
      transform: none;
      transition: none;
    }
  }
`;
type Props = {};
type Ref = {
  open: () => void;
};
export const useModalQuickScanProductRef = () => useRef<Ref>(null);
const ModalQuickScanProduct = forwardRef<Ref, Props>(({ }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocus, setIsFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [productDetail, setProductDetail] = useState<IProductItem | null>(null);
  const onClose = () => {
    cashierScannerCtrl.current?.setForceStop(false);
    setOpen(false);
  };
  const onOpen: Ref['open'] = () => {
    cashierScannerCtrl.current?.setForceStop(true);
    setOpen(true);
    delay(() => inputRef.current?.focus(), 100);
  };
  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!event.target) return;
      const input = event.target as HTMLInputElement;
      const code: string = input.value;
      if (!code?.trim()) return;
      input.value = '';
      setOpen(false);
      setPageLoading(true);
      try {
        const res: IResponseData<IProductItem[]> = await cashierApis.getProductByCode(code);
        const product = first(res.data.data);
        if (product?.productCode === code) {
          return setProductDetail(product);
        }
        throw 'fail';
      } catch (error) {
        toast.error('Not Found!');
      } finally {
        setPageLoading(false);
      }
      return;
    }
  };
  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  return (
    <>
      <Modal title='Add Bin' open={open} onClose={onClose} width={'auto'} styles={{
        header: { display: 'none' },
        content: { overflow: 'unset', borderRadius: 8, },
        body: {
          borderRadius: 8,
          maxHeight: 'unset', overflow: 'unset',
          position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }
      }}>
        <Container htmlFor='scanQRProduct'>
          <img className={clsx(isFocus ? 'animation-pulse' : '')} src={scan_gif} alt="scan_gif" style={{ width: '140px', height: 'auto' }} />
          <input
            id='scanQRProduct'
            style={{ height: 0, width: 0, opacity: 0 }}
            onKeyDown={handleKeyDown}
            onBlur={() => setIsFocus(false)} onFocus={() => setIsFocus(true)} ref={inputRef} type="text" />
          <Text>{isFocus ? 'Please scan ...' : 'Click here to scan'}</Text>
        </Container>
        <IconClose className='icon-close' onClick={onClose}><img src={close} style={{ height: '32px', width: '32px' }} /></IconClose>
      </Modal>
      <ModalProductInfo
        data={productDetail}
        visible={!!productDetail}
        onClose={() => setProductDetail(null)}
      />
    </>
  );
});
ModalQuickScanProduct.displayName = 'ModalQuickScanProduct';
export default ModalQuickScanProduct;